import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedIcon from "@mui/icons-material/Verified";
import Atras from "./atras";
import { confirmClaimVerification, getClaimOptions, startClaimVerification } from "../firebase";

// Callable errors come back as "functions/<code>" with the server's Spanish message.
const errorMessage = (error) => error?.message || "Ocurrió un error. Probá de nuevo.";

const MethodButton = ({ icon, title, detail, onClick, disabled }) => (
  <Paper sx={{ mt: 1.5, borderRadius: 3, overflow: "hidden" }}>
    <Button
      fullWidth
      onClick={onClick}
      disabled={disabled}
      startIcon={icon}
      sx={{ justifyContent: "flex-start", px: 2.5, py: 1.75, textAlign: "left", color: "text.primary" }}
    >
      <Box>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </Box>
    </Button>
  </Paper>
);

/**
 * Owner verification for a publication that claims a Google hotel.
 * Steps: choose a method → (email: enter an address on the hotel's domain) → enter the code.
 */
const ClaimVerification = () => {
  const { placeId } = useParams();
  const navigate = useNavigate();

  const [options, setOptions] = useState(null);
  const [step, setStep] = useState("choose"); // choose | email | code | done
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [lastRequest, setLastRequest] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getClaimOptions({ placeId })
      .then(setOptions)
      .catch((err) => {
        if (err.code === "functions/already-exists") setStep("done");
        else setError(errorMessage(err));
        setOptions({});
      });
  }, [placeId]);

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (request) =>
    run(async () => {
      const result = await startClaimVerification({ placeId, ...request });
      setLastRequest(request);
      setSentTo(result.sentTo);
      setCode("");
      setStep("code");
    });

  const confirm = () =>
    run(async () => {
      await confirmClaimVerification({ placeId, code });
      setStep("done");
    });

  const canVerify = options && (options.emailDomain || options.maskedPhone);

  let content;
  if (!options) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  } else if (step === "done") {
    content = (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <VerifiedIcon color="primary" sx={{ fontSize: 64 }} />
        <Typography variant="h5" sx={{ mt: 1 }}>
          ¡Alojamiento verificado!
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Tu publicación ahora aparece en el mapa como {options.hotelName || "tu alojamiento"}.
        </Typography>
        <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate("/home")}>
          Ir al mapa
        </Button>
      </Box>
    );
  } else if (!canVerify) {
    content = (
      <Alert severity="info" sx={{ mt: 2 }}>
        Este alojamiento no tiene un sitio web propio ni un teléfono en Google, así que lo vamos a
        verificar manualmente. Te vamos a contactar.
      </Alert>
    );
  } else if (step === "choose") {
    content = (
      <>
        <Typography color="text.secondary">
          Para mostrar tu publicación como <strong>{options.hotelName}</strong>, confirmá que sos el
          dueño. Te mandamos un código a un contacto oficial del alojamiento.
        </Typography>
        {options.emailDomain && (
          <MethodButton
            icon={<EmailIcon />}
            title="Email del alojamiento"
            detail={`Una dirección @${options.emailDomain}`}
            onClick={() => setStep("email")}
            disabled={busy}
          />
        )}
        {options.maskedPhone && (
          <>
            <MethodButton
              icon={<SmsIcon />}
              title="SMS al teléfono del alojamiento"
              detail={options.maskedPhone}
              onClick={() => sendCode({ method: "phone", channel: "sms" })}
              disabled={busy}
            />
            <MethodButton
              icon={<PhoneIcon />}
              title="Llamada al teléfono del alojamiento"
              detail={`${options.maskedPhone} · para teléfonos fijos`}
              onClick={() => sendCode({ method: "phone", channel: "call" })}
              disabled={busy}
            />
          </>
        )}
      </>
    );
  } else if (step === "email") {
    content = (
      <>
        <Typography color="text.secondary">
          Ingresá un email de <strong>@{options.emailDomain}</strong>, el dominio del sitio web del
          alojamiento en Google.
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          type="email"
          label="Email del alojamiento"
          placeholder={`reservas@${options.emailDomain}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
        <Button
          fullWidth
          variant="contained"
          disabled={busy || !email.trim()}
          onClick={() => sendCode({ method: "email", email })}
        >
          {busy ? <CircularProgress size={22} color="inherit" /> : "Enviar código"}
        </Button>
        <Button fullWidth sx={{ mt: 1 }} onClick={() => setStep("choose")} disabled={busy}>
          Elegir otro método
        </Button>
      </>
    );
  } else {
    content = (
      <>
        <Typography color="text.secondary">
          {lastRequest?.channel === "call"
            ? `Te estamos llamando al ${sentTo}. Anotá el código que te dicten.`
            : `Te mandamos un código a ${sentTo}.`}
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          label="Código"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
          disabled={busy}
        />
        <Button fullWidth variant="contained" disabled={busy || code.length < 4} onClick={confirm}>
          {busy ? <CircularProgress size={22} color="inherit" /> : "Verificar"}
        </Button>
        <Button fullWidth sx={{ mt: 1 }} onClick={() => sendCode(lastRequest)} disabled={busy}>
          Reenviar código
        </Button>
        <Button fullWidth onClick={() => setStep("choose")} disabled={busy}>
          Elegir otro método
        </Button>
      </>
    );
  }

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar sx={{ gap: 2 }}>
          <Atras />
          <Typography variant="h6">Verificar alojamiento</Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 12, mb: 12, maxWidth: 520 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {content}
      </Container>
    </Container>
  );
};

export default ClaimVerification;
